import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import type { QueuedItem } from './queue';
import { createMutableQueue, createImmutableQueue } from './queue';
export interface QueueHook<T> {
  addById: (id: string, data: T) => void;
  add: (data: T) => void;
  remove: (id: string) => void;
  removeAll: () => void;
  entries: QueuedItem<T>[];
}

/**
 * This hook lets you create a notification queue that can be manipulated. It accepts a notification type that describes
 * the shape of every notification that is allowed to be emitted. This doesn't provide any UI for the notifications.
 * This is left up to the end user to implement.
 *
 * This means you'll need to create your own NotificationProvider to create the queue, render the notifications, and
 * provide the queue API to your components so they can add notifications.
 *
 *      interface Data {
 *        message: string;
 *      }
 *
 *      function MyNotificationProvider() {
 *        const queue = useQueue<Data>();
 *
 *        return (
 *          queue.list.map(item => {
 *            return <div>{item.message}</div>
 *          })
 *        )
 *      }
 */
export function useImmutableQueue() {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createImmutableQueue();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  t0 = t1;
  const initialQueue = t0;
  const [queue, setQueue] = useState(initialQueue);
  let t2;
  if ($[1] !== queue.entries) {
    t2 = {
      addById(id, data) {
        setQueue(list => list.addById(id, data));
      },
      add(data_0) {
        setQueue(list_0 => list_0.add(data_0));
      },
      remove(id_0) {
        setQueue(list_1 => list_1.remove(id_0));
      },
      removeAll() {
        setQueue(_temp);
      },
      entries: queue.entries
    };
    $[1] = queue.entries;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(list_2) {
  return list_2.removeAll();
}
export function useMutableQueue() {
  const $ = _c(1);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createMutableQueue();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  t0 = t1;
  return t0;
}